/**
 * Creates a `ReadableStream` that yields byte data using the provided custom handler.
 *
 * The resulting stream can provide a BYOB reader if supported by the runtime.
 * If unsupported, only a default reader is available.
 *
 * @param handler - The stream handler: `read`, `release`. See `CreateReadableStreamHandler` for details.
 * @param options - Optional settings: `signal`, `checkSignalEarly`. See `CreateReadableStreamOptions` for details.
 * @returns A `ReadableStream<Uint8Array<ArrayBuffer>>` configured with the provided handler and options.
 */
function createReadableStream(handler, options) {
    const checkSignalEarly = options?.checkSignalEarly ?? true;
    if (checkSignalEarly) {
        throwIfAborted(options?.signal);
    }
    const read = handler.read;
    const release = handler.release;
    let abortListener = null;
    let buffer = null;
    let cleanupPromise = null;
    function cleanup(type, reason) {
        if (cleanupPromise === null) {
            cleanupPromise = (async () => {
                buffer = null;
                if (options?.signal != null && abortListener != null) {
                    options.signal.removeEventListener("abort", abortListener);
                    abortListener = null;
                }
                if (release != null) {
                    await release(type, reason);
                }
            })();
        }
        return cleanupPromise;
    }
    const useByteStream = (options?.__internal_useByteStream__ !== undefined)
        ? options.__internal_useByteStream__
        : isReadableByteStreamAvailable();
    if (!useByteStream) {
        return new ReadableStream({
            start(controller) {
                if (options?.signal != null) {
                    abortListener = () => {
                        const reason = options?.signal?.reason ?? newAbortSignalDefaultError();
                        cleanup("SignalAbort", reason).catch(() => { });
                        controller.error(reason);
                    };
                    options?.signal?.addEventListener("abort", abortListener);
                }
            },
            async pull(controller) {
                try {
                    throwIfAborted(options?.signal);
                    const data = await read();
                    throwIfAborted(options?.signal);
                    if (data == null || data.byteLength === 0) {
                        await cleanup("Close");
                        controller.close();
                        return;
                    }
                    controller.enqueue(data);
                }
                catch (e) {
                    const isSignalAbort = isThrownByAbortSignal(e, options?.signal);
                    await cleanup(isSignalAbort ? "SignalAbort" : "Error", e).catch(() => { });
                    throw e;
                }
            },
            async cancel(reason) {
                await cleanup("Cancel", reason);
            }
        });
    }
    // autoAllocateChunkSize を指定すると stream.getReader() でも byob が使われるようになるが、
    // この実装で byob を用いてもコピーが増えるだけで恩恵が少ないため指定しない。
    // また type: "bytes" で strategy を指定すると (正確には size を定義すると) エラーになる点にも注意。
    return new ReadableStream({
        type: "bytes",
        start(controller) {
            if (options?.signal) {
                abortListener = () => {
                    const reason = options?.signal?.reason ?? newAbortSignalDefaultError();
                    cleanup("SignalAbort", reason).catch(() => { });
                    controller.error(reason);
                };
                options?.signal.addEventListener("abort", abortListener);
            }
        },
        async pull(controller) {
            try {
                throwIfAborted(options?.signal);
                if (buffer == null || buffer.byteLength === 0) {
                    buffer = (await read()) ?? null;
                    throwIfAborted(options?.signal);
                }
                if (buffer == null || buffer.byteLength === 0) {
                    await cleanup("Close");
                    // byobRequest がある場合、respond を呼ばないと promise　が解決されない。
                    // controller.close() の後だと respond(0) を読んでもエラーにはならない。
                    // https://github.com/whatwg/streams/issues/1170
                    controller.close();
                    controller.byobRequest?.respond(0);
                    return;
                }
                const byob = controller.byobRequest;
                // byobRequest がある場合、respond を呼ばないと promise　が解決されないことに注意
                if (byob != null) {
                    // respond する前なので null にならない
                    const v = byob.view;
                    const view = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
                    const nread = Math.min(buffer.byteLength, view.byteLength);
                    view.set(buffer.subarray(0, nread));
                    buffer = buffer.subarray(nread);
                    throwIfAborted(options?.signal);
                    byob.respond(nread);
                }
                else {
                    throwIfAborted(options?.signal);
                    controller.enqueue(buffer);
                    buffer = null;
                }
            }
            catch (e) {
                const isSignalAbort = isThrownByAbortSignal(e, options?.signal);
                await cleanup(isSignalAbort ? "SignalAbort" : "Error", e).catch(() => { });
                // byobRequest が存在する場合、controller.close() を呼んだだけでは
                // Promise は解決されず、respond() も呼ぶ必要がある。
                // controller.error() も同様の挙動になる可能性がある。(要検証)
                // 少なくとも throw すれば Promise は解決されるため、現状はこの実装とする。
                throw e;
            }
        },
        async cancel(reason) {
            await cleanup("Cancel", reason);
        }
    });
}
/**
 * Creates a `ReadableStream` that yields byte data using a BYOB-style handler.
 *
 * The resulting stream can provide a BYOB reader if supported by the runtime.
 * If unsupported, only a default reader is available.
 *
 * @param handler - The stream handler: `read`, `release`. See `CreateBYOBReadableStreamHandler` for details.
 * @param defaultBufferSize - The size of the fallback buffer passed to `handler.read`. Must be a positive safe integer. Used as `autoAllocateChunkSize` when a bytes-type reader is available. If unsupported, used as the size of the internal buffer for a default reader.
 * @param options - Optional settings: `signal`, `checkSignalEarly`. See `CreateBYOBReadableStreamOptions` for details.
 * @returns A `ReadableStream<Uint8Array<ArrayBuffer>>` configured with the provided handler and options.
 */
function createBYOBReadableStream(handler, defaultBufferSize, options) {
    const checkSignalEarly = options?.checkSignalEarly ?? true;
    if (checkSignalEarly) {
        throwIfAborted(options?.signal);
    }
    requiresNonzeroSafeInt(defaultBufferSize, "defaultBufferSize");
    const read = handler.read;
    const release = handler.release;
    let abortListener = null;
    let buffer = null;
    let cleanupPromise = null;
    function cleanup(type, reason) {
        if (cleanupPromise === null) {
            cleanupPromise = (async () => {
                buffer = null;
                if (options?.signal != null && abortListener != null) {
                    options.signal.removeEventListener("abort", abortListener);
                    abortListener = null;
                }
                if (release != null) {
                    await release(type, reason);
                }
            })();
        }
        return cleanupPromise;
    }
    const useByteStream = (options?.__internal_useByteStream__ !== undefined)
        ? options.__internal_useByteStream__
        : isReadableByteStreamAvailable();
    if (!useByteStream) {
        return new ReadableStream({
            start(controller) {
                if (options?.signal != null) {
                    abortListener = () => {
                        const reason = options?.signal?.reason ?? newAbortSignalDefaultError();
                        cleanup("SignalAbort", reason).catch(() => { });
                        controller.error(reason);
                    };
                    options?.signal?.addEventListener("abort", abortListener);
                }
            },
            async pull(controller) {
                try {
                    throwIfAborted(options?.signal);
                    if (buffer === null) {
                        buffer = new Uint8Array(defaultBufferSize);
                    }
                    throwIfAborted(options?.signal);
                    const nread = await read(buffer);
                    throwIfAborted(options?.signal);
                    requiresSafeUint(nread, "nread");
                    if (nread === 0) {
                        await cleanup("Close");
                        controller.close();
                        return;
                    }
                    controller.enqueue(buffer.slice(0, nread));
                }
                catch (e) {
                    const isSignalAbort = isThrownByAbortSignal(e, options?.signal);
                    await cleanup(isSignalAbort ? "SignalAbort" : "Error", e).catch(() => { });
                    throw e;
                }
            },
            async cancel(reason) {
                await cleanup("Cancel", reason);
            }
        });
    }
    return new ReadableStream({
        type: "bytes",
        autoAllocateChunkSize: defaultBufferSize,
        start(controller) {
            if (options?.signal) {
                abortListener = () => {
                    const reason = options?.signal?.reason ?? newAbortSignalDefaultError();
                    cleanup("SignalAbort", reason).catch(() => { });
                    controller.error(reason);
                };
                options?.signal.addEventListener("abort", abortListener);
            }
        },
        async pull(controller) {
            try {
                throwIfAborted(options?.signal);
                const byob = controller.byobRequest;
                if (byob == null) {
                    if (buffer == null) {
                        buffer = new Uint8Array(defaultBufferSize);
                    }
                    throwIfAborted(options?.signal);
                    const nread = await read(buffer);
                    throwIfAborted(options?.signal);
                    requiresSafeUint(nread, "nread");
                    if (nread === 0) {
                        await cleanup("Close");
                        controller.close();
                        return;
                    }
                    controller.enqueue(buffer.slice(0, nread));
                    return;
                }
                const v = byob.view;
                if (v == null)
                    return;
                const view = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
                throwIfAborted(options?.signal);
                const nread = await read(view);
                throwIfAborted(options?.signal);
                requiresSafeUint(nread, "nread");
                if (nread === 0) {
                    await cleanup("Close");
                    // byobRequest がある場合、respond を呼ばないと promise　が解決されない。
                    // controller.close() の後だと respond(0) を読んでもエラーにはならない。
                    // https://github.com/whatwg/streams/issues/1170
                    controller.close();
                    controller.byobRequest?.respond(0);
                    return;
                }
                byob.respond(nread);
            }
            catch (e) {
                const isSignalAbort = isThrownByAbortSignal(e, options?.signal);
                await cleanup(isSignalAbort ? "SignalAbort" : "Error", e).catch(() => { });
                throw e;
            }
        },
        async cancel(reason) {
            await cleanup("Cancel", reason);
        }
    });
}
/**
 * Creates a `WritableStream` that writes byte data using the provided custom handler.
 *
 * @param handler - The stream handler: `write`, `release`. See `CreateWritableStreamHandler` for details.
 * @param options - Optional settings: `signal`, `checkSignalEarly`, `bufferSize`, `strictBufferSize`, `useBufferView`. See `CreateWritableStreamOptions` for details.
 * @returns A `WritableStream<Uint8Array<ArrayBufferLike>>` instance configured with the provided handler and options.
 */
function createWritableStream(handler, options) {
    const checkSignalEarly = options?.checkSignalEarly ?? true;
    if (checkSignalEarly) {
        throwIfAborted(options?.signal);
    }
    const write = handler.write;
    const release = handler.release;
    const bufferSize = options?.bufferSize ?? 0;
    requiresSafeUint(bufferSize, "bufferSize");
    let abortListener = null;
    let buffer = null;
    let bufferOffset = 0;
    let cleanupPromise = null;
    function cleanup(type, reason) {
        if (cleanupPromise === null) {
            cleanupPromise = (async () => {
                buffer = null;
                if (options?.signal != null && abortListener != null) {
                    options?.signal?.removeEventListener("abort", abortListener);
                    abortListener = null;
                }
                if (release != null) {
                    await release(type, reason);
                }
            })();
        }
        return cleanupPromise;
    }
    return new WritableStream({
        start(controller) {
            if (options?.signal != null) {
                abortListener = () => {
                    const reason = options?.signal?.reason ?? newAbortSignalDefaultError();
                    cleanup("SignalAbort", reason).catch(() => { });
                    controller.error(reason);
                };
                options?.signal.addEventListener("abort", abortListener);
            }
        },
        async write(src) {
            try {
                throwIfAborted(options?.signal);
                // bufferSize が 0 以下の場合や src が buffer より大きい場合は buffer を使わずに処理する。
                if (bufferSize <= 0 ||
                    (bufferSize <= src.byteLength && options?.strictBufferSize !== true)) {
                    // buffer に既にデータがある場合、それを処理する。
                    if (buffer !== null && 0 < bufferOffset) {
                        const chunk = options?.useBufferView === true
                            ? buffer.subarray(0, bufferOffset)
                            : buffer.slice(0, bufferOffset);
                        throwIfAborted(options?.signal);
                        await write(chunk);
                        bufferOffset = 0;
                    }
                    throwIfAborted(options?.signal);
                    await write(mapToArrayBuffer(src));
                    return;
                }
                let srcOffset = 0;
                while (srcOffset < src.byteLength) {
                    throwIfAborted(options?.signal);
                    if (buffer === null) {
                        buffer = new Uint8Array(bufferSize);
                    }
                    const n = Math.min(bufferSize - bufferOffset, src.byteLength - srcOffset);
                    buffer.set(src.subarray(srcOffset, srcOffset + n), bufferOffset);
                    bufferOffset += n;
                    srcOffset += n;
                    if (bufferOffset === bufferSize) {
                        throwIfAborted(options?.signal);
                        const chunk = buffer;
                        if (options?.useBufferView !== true) {
                            buffer = null;
                        }
                        await write(chunk);
                        bufferOffset = 0;
                    }
                }
            }
            catch (e) {
                const isSignalAbort = isThrownByAbortSignal(e, options?.signal);
                await cleanup(isSignalAbort ? "SignalAbort" : "Error", e).catch(() => { });
                throw e;
            }
        },
        async close() {
            try {
                if (0 < bufferOffset && buffer != null) {
                    const view = buffer.subarray(0, bufferOffset);
                    buffer = null;
                    await write(view);
                }
                await cleanup("Close");
            }
            catch (e) {
                await cleanup("Error", e).catch(() => { });
                throw e;
            }
        },
        async abort(reason) {
            await cleanup("Abort", reason);
        }
    });
}
let _isReadableByteStreamAvailable = null;
function isReadableByteStreamAvailable() {
    if (_isReadableByteStreamAvailable === null) {
        try {
            new ReadableStream({ type: "bytes" });
            _isReadableByteStreamAvailable = true;
        }
        catch {
            _isReadableByteStreamAvailable = false;
        }
    }
    return _isReadableByteStreamAvailable;
}
function throwIfAborted(signal) {
    if (signal?.aborted === true) {
        throw (signal?.reason ?? newAbortSignalDefaultError());
    }
}
function newAbortSignalDefaultError() {
    return new DOMException("The operation was aborted.", "AbortError");
}
function isThrownByAbortSignal(err, signal) {
    return (signal?.aborted === true) &&
        (err === signal.reason || (err instanceof DOMException && err.name === "AbortError"));
}
function mapToArrayBuffer(buffer) {
    return buffer.buffer instanceof ArrayBuffer
        ? buffer
        : new Uint8Array(buffer);
}
function requiresSafeUint(num, numName) {
    const name = numName ?? "value";
    if (!Number.isSafeInteger(num)) {
        throw new TypeError(`${name} must be a safe integer.`);
    }
    if (num < 0) {
        throw new RangeError(`${name} must be a positive integer.`);
    }
}
function requiresNonzeroSafeInt(num, numName) {
    const name = numName;
    if (!Number.isSafeInteger(num)) {
        throw new TypeError(`${name} must be a safe integer.`);
    }
    if (num <= 0) {
        throw new RangeError(`${name} must be a non-zero positive integer.`);
    }
}

export { createBYOBReadableStream, createReadableStream, createWritableStream };
//# sourceMappingURL=index.js.map
